import { inject, Injectable } from "@angular/core";
import {
  Action,
  Actions,
  createSelector,
  NgxsOnInit,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  Store
} from "@ngxs/store";
import { UserAreaDetailMenuEnum } from "../../enums/rights/UserAreaDetailMenuEnum";
import { UserAreaDTO } from "../../models/rights/user-area/UserAreaDTO";
import { UserAreaDetailActions } from "./user-area-detail.action";
import { finalize, Observable } from "rxjs";
import { UserAreaService } from "../../services/user-area.service";
import { map, takeUntil } from "rxjs/operators";
import { UserAreaType } from "../../models/rights/user-area/user-area-type.enum";
import Constants from "../../Constants";
import { FilterPageNameEnum } from "../../enums/dynamic-filter/FilterPageNameEnum";
import { CompoApp } from "../../services/CompoApp";
import { DynamicFilterRequestDto } from "../../models/dynamic-filter/dynamic-filter-request-dto.model";
import { UserAreaEditorActions } from "../user-area-editor/user-area-editor.action";
import { ResetStateDefaults } from "../base-action-class.model";
import { AuthserviceService } from "../../content/pages-new-design/auth-page/login-page/authservice.service";
import { UnsubscribeBaseServiceModel } from "../../services/client/UnsubscribeBaseService.model";
import { DynamicFilterTemplateService } from '../../services/dynamic-filter-template.service';
import { UserAreaDefinition } from '../../enums/rights/user-area-definition.enum';
import { DynamicFilterTemplatePage } from '../../models/dynamic-filter/dynamic-filter-template-page.model';

interface UserAreaDetailStateModel {
  page: UserAreaDetailMenuEnum;
  userAreaId: string;
  userArea: UserAreaDTO;
  requestInProgress: boolean;
  accessibleObjectPageFilter: DynamicFilterTemplatePage;
  userAreaObjectPageFilter: DynamicFilterTemplatePage;
}

const getDefaults = (): Partial<UserAreaDetailStateModel> => ({
  page: UserAreaDetailMenuEnum.BasisInfo,
  userAreaId: null,
  userArea: null,
  requestInProgress: false
});

@Injectable()
@State<UserAreaDetailStateModel>({
  name: 'userAreaDetail',
  defaults: {
    ...getDefaults,
    accessibleObjectPageFilter: null,
    userAreaObjectPageFilter: null
  } as UserAreaDetailStateModel
})
export class UserAreaDetailXsState extends UnsubscribeBaseServiceModel implements NgxsOnInit {

  //region INJECTIONS
  private _dynamicFilterTemplateService = inject(DynamicFilterTemplateService);
  private authService = inject(AuthserviceService);
  private userAreaService = inject(UserAreaService);
  //endregion

  constructor(
    private app: CompoApp,
    private actions: Actions,
    private store: Store
  ) {
    super();
    this.actions.pipe(
      ofActionSuccessful(UserAreaEditorActions.Save)
    )
      .subscribe(() => this.store.dispatch(new UserAreaDetailActions.ReFetch()));
  }

  ngxsOnInit(ctx: StateContext<any>): void {
    this.initFilterTemplates(ctx);

    this.app.on.loginTrigger
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => this.initFilterTemplates(ctx));
  }

  private initFilterTemplates(ctx: StateContext<any>) {
    if (this.authService.isAuthenticated()) {
      this._dynamicFilterTemplateService.getByPageEnum(FilterPageNameEnum.USER_ACCESSIBLE_OBJECTS)
        .then(resp => {
          ctx.patchState({
            accessibleObjectPageFilter: resp
          });
        });

      this._dynamicFilterTemplateService.getByPageEnum(FilterPageNameEnum.USER_AREA_DETAIL_OBJECTS)
        .then(resp => {
          ctx.patchState({
            userAreaObjectPageFilter: resp
          });
        });
    }
  }

  static generateFilterDtoBy(
    searchTerm: string = '',
    excludeExistingObjectIds: boolean = false,
    parentId: string = null
  ) {
    return createSelector(
      [UserAreaDetailXsState],
      (state: UserAreaDetailStateModel): DynamicFilterRequestDto => {
        return this.generateFilterDtoForAccessibleObjects(
          state,
          searchTerm,
          excludeExistingObjectIds,
          parentId
        );
      });
  }

  static generateFilterDtoByUserArea(
    searchTerm: string = '',
    parentId: string = null
  ) {
    return createSelector(
      [UserAreaDetailXsState],
      (state: UserAreaDetailStateModel): DynamicFilterRequestDto => {
        return this.generateFilterDtoForUserAreaObjects(
          state,
          searchTerm,
          parentId
        );
      });
  }

  @Selector()
  static pageOptions(state: UserAreaDetailStateModel): { label: string, value: UserAreaDetailMenuEnum }[] {
    if (!state.userArea) {
      return Constants.DROPDOWNS.userAreaDetailBasisInfoOnlyMenuEntries;
    }
    if (state.userArea.definition === UserAreaDefinition.NONE) {
      if (state?.userArea.type) {
        switch (state.userArea.type) {
          case UserAreaType.NONE: {
            return Constants.DROPDOWNS.userAreaDetailMenuEntries;
          }
          case UserAreaType.COMBINATION: {
            return Constants.DROPDOWNS.userAreaDetailCombinationMenuEntries;
          }
          case UserAreaType.SINGLE_SELECTION_OBJECT: {
            return Constants.DROPDOWNS.userAreaDetailSingleSelectionObjectMenuEntries;
          }
          case UserAreaType.SINGLE_SELECTION_MODULE: {
            return Constants.DROPDOWNS.userAreaDetailSingleSelectionModuleMenuEntries;
          }
          case UserAreaType.SINGLE_SELECTION_SERVICE_PROVIDER: {
            return Constants.DROPDOWNS.userAreaDetailSingleSelectionServiceProviderMenuEntries;
          }
        }
      }
    } else if (state.userArea.definition === UserAreaDefinition.AUTO_SERVICE_PROVIDER) {
      return Constants.DROPDOWNS.userAreaDetailAutoServiceProviderMenuEntries;
    } else if (state.userArea.definition === UserAreaDefinition.AUTO_COMMUNITY) {
      return Constants.DROPDOWNS.userAreaDetailAutoCommunityMenuEntries;
    } else if (state.userArea.definition === UserAreaDefinition.AUTO) {
      return Constants.DROPDOWNS.userAreaDetailMenuEntries;
    }

    //Default - fallback
    return Constants.DROPDOWNS.userAreaDetailBasisInfoOnlyMenuEntries;
  }

  @Selector()
  static page(state: UserAreaDetailStateModel): UserAreaDetailMenuEnum {
    return state.page;
  }

  @Selector()
  static userAreaId(state: UserAreaDetailStateModel): string {
    return state.userAreaId;
  }

  @Selector()
  static userArea(state: UserAreaDetailStateModel): UserAreaDTO {
    return state.userArea;
  }

  @Action(UserAreaDetailActions.Fetch)
  public fetch(
    ctx: StateContext<UserAreaDetailStateModel>,
    action: UserAreaDetailActions.Fetch
  ): Observable<void> {
    const state: UserAreaDetailStateModel = ctx.getState();

    if (state.userAreaId && state.userAreaId === action.userAreaId) {
      return;
    }

    if (state.requestInProgress) {
      throw new Error('User area request is in progress!');
    }

    if (!action.userAreaId) {
      throw new Error('User area id cannot be null!');
    }

    ctx.patchState({
      ...getDefaults(),
      requestInProgress: true
    });

    return this.userAreaService.getById(action.userAreaId)
      .pipe(
        map(userArea => {
          ctx.patchState({
            page: UserAreaDetailMenuEnum.BasisInfo,
            userAreaId: userArea.id,
            userArea: userArea
          });
        }),
        finalize(() => ctx.patchState({requestInProgress: false}))
      );
  }

  @Action(UserAreaDetailActions.SetPage)
  public setPage(
    ctx: StateContext<UserAreaDetailStateModel>,
    action: UserAreaDetailActions.SetPage
  ): void {
    const state: UserAreaDetailStateModel = ctx.getState();

    if (state.requestInProgress) {
      throw new Error('User area request is in progress!');
    }

    if (action.page === null || action.page === undefined) {
      throw new Error('Page cannot be null!');
    }

    ctx.patchState({
      page: action.page
    });
  }

  @Action(UserAreaDetailActions.UpdateBasisInfo)
  public updateBasisInfo(
    ctx: StateContext<UserAreaDetailStateModel>,
    action: UserAreaDetailActions.UpdateBasisInfo
  ): Observable<void> {
    const state: UserAreaDetailStateModel = ctx.getState();
    const updatedUserArea: UserAreaDTO = action.userArea;

    if (state.requestInProgress) {
      throw new Error('User area request is in progress!');
    }

    if (!state.userArea) {
      throw new Error('User area is not initialized!');
    }

    if (!updatedUserArea) {
      throw new Error('User area cannot be null!');
    }

    const userArea: UserAreaDTO = {
      ...state.userArea,
      organizationId: updatedUserArea.organizationId,
      isSystem: updatedUserArea.isSystem,
      nameTranslated: updatedUserArea.nameTranslated,
      nameCV: updatedUserArea.nameCV
    } as UserAreaDTO;

    ctx.patchState({
      requestInProgress: false
    });

    return this.userAreaService.update(userArea)
      .pipe(
        map(() => {
          ctx.patchState({userArea});
        }),
        finalize(() => ctx.patchState({requestInProgress: false}))
      );
  }

  @Action(UserAreaDetailActions.UpdateActualization)
  public updateActualization(
    ctx: StateContext<UserAreaDetailStateModel>,
    action: UserAreaDetailActions.UpdateActualization
  ): Observable<void> {
    const state: UserAreaDetailStateModel = ctx.getState();
    const updatedUserArea: UserAreaDTO = action.userArea;

    if (state.requestInProgress) {
      throw new Error('User area request is in progress!');
    }

    if (!state.userArea) {
      throw new Error('User area is not initialized!');
    }

    if (!updatedUserArea) {
      throw new Error('User area cannot be null!');
    }

    const userArea: UserAreaDTO = {
      ...state.userArea,
      addNewOrganizationSubOrganizationMapping: updatedUserArea.addNewOrganizationSubOrganizationMapping,
      addNewOrganizationLocationMapping: updatedUserArea.addNewOrganizationLocationMapping,
      addNewOrganizationObjectMapping: updatedUserArea.addNewOrganizationObjectMapping,
      addNewOrganizationOrganizationModuleMapping: updatedUserArea.addNewOrganizationOrganizationModuleMapping,
      addNewOrganizationCommunityModuleMapping: updatedUserArea.addNewOrganizationCommunityModuleMapping,
      addNewLocationSubLocationMapping: updatedUserArea.addNewLocationSubLocationMapping,
      addNewLocationObjectMapping: updatedUserArea.addNewLocationObjectMapping,
      addNewClassSubClassMapping: updatedUserArea.addNewClassSubClassMapping,
      addNewClassObjectMapping: updatedUserArea.addNewClassObjectMapping,
      addNewObjectSubObjectMapping: updatedUserArea.addNewObjectSubObjectMapping,
      addMovedObjectOrganizationMapping: updatedUserArea.addMovedObjectOrganizationMapping,
      addMovedObjectLocationMapping: updatedUserArea.addMovedObjectLocationMapping,
      addMovedObjectObjectMapping: updatedUserArea.addMovedObjectObjectMapping,
      addMovedObjectClassMapping: updatedUserArea.addMovedObjectClassMapping
    } as UserAreaDTO;

    ctx.patchState({
      requestInProgress: false
    });

    return this.userAreaService.update(userArea)
      .pipe(
        map((userArea) => {
          ctx.patchState({userArea});
        }),
        finalize(() => ctx.patchState({requestInProgress: false}))
      );
  }

  @Action(UserAreaDetailActions.Reset)
  public reset(
    ctx: StateContext<UserAreaDetailStateModel>
  ): void {
    const state = ctx.getState();

    if (state.requestInProgress) {
      throw new Error('User area request is in progress!');
    }

    ctx.patchState({
      ...getDefaults()
    });
  }

  @Action(ResetStateDefaults)
  public resetDefaults(
    ctx: StateContext<UserAreaDetailStateModel>
  ): void {
    ctx.patchState({
      ...getDefaults()
    });
  }

  @Action(UserAreaDetailActions.ReFetch)
  public reFetch(
    ctx: StateContext<UserAreaDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();

    if (state.requestInProgress || !state.userAreaId) {
      return;
    }

    ctx.patchState({
      requestInProgress: true
    });

    return this.userAreaService.getById(state.userAreaId)
      .pipe(
        map(userArea => {
          ctx.patchState({
            userArea: userArea
          });
        }),
        finalize(() => ctx.patchState({requestInProgress: false}))
      );
  }

  private static generateFilterDtoForAccessibleObjects(
    state: UserAreaDetailStateModel,
    searchTerm: string,
    excludeExistingObjectIds: boolean = false,
    parentId: string = null
  ) {
    const dto = new DynamicFilterRequestDto();

    if (!state.accessibleObjectPageFilter?.id || !state.userArea) {
      return null;
    }

    dto.templateId = state.accessibleObjectPageFilter.id;
    const searchTermFilter = state.accessibleObjectPageFilter.filterEntries.SEARCH_TERM;
    const filterValues = {};
    if (searchTermFilter && !!searchTerm) {
      filterValues[searchTermFilter.id] = searchTerm;
    }
    dto.filterValues = DynamicFilterRequestDto.initFilterValues(
      state.accessibleObjectPageFilter.filterEntries,
      filterValues
    );
    if (excludeExistingObjectIds) {
      dto.excludedIds = state.userArea?.objects?.map(o => o.objectId);
    }
    dto.parentId = parentId;
    return dto;
  }

  private static generateFilterDtoForUserAreaObjects(
    state: UserAreaDetailStateModel,
    searchTerm: string,
    parentId: string = null
  ): DynamicFilterRequestDto {
    const dto = new DynamicFilterRequestDto();

    if (!state.userAreaObjectPageFilter?.id || !state.userArea) {
      return null;
    }

    dto.templateId = state.userAreaObjectPageFilter.id;
    const searchTermFilter = state.userAreaObjectPageFilter.filterEntries.SEARCH_TERM;
    const filterValues = {};
    if (searchTermFilter && !!searchTerm) {
      filterValues[searchTermFilter.id] = searchTerm;
    }
    dto.filterValues = DynamicFilterRequestDto
      .initFilterValues(state.userAreaObjectPageFilter.filterEntries, filterValues);
    dto.parentId = parentId;
    dto.detailId = state.userAreaId;
    return dto;
  }
}
